import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '@/components/Modal';
import { feedbackApi, type BoardItem, type FeedbackItem } from '@/lib/apiClient';
import { snippet, wantsLabel } from '@/lib/everyonesFeedback';
import { BOARD_KEY, CATEGORY_LABEL, MINE_KEY, fmtWhen, statusLabel } from '@/lib/feedbackMine';
import { plural } from '@/lib/plural';
import { mayHaveBeenApplied } from './feedbackBoard';
import { SEARCH_SHOWN, describeMerge, indexCandidates, mergeCandidates, mergeHeading, previewLines, searchTargets, suggestTargets } from './feedbackMerge';

/**
 * Its own `fmg-` styles, declared here: a class in this app is live only while the component that declares it is mounted (there are no CSS Modules), so this
 * dialog does not lean on the Feedback tab's or the board dialogs' rules.
 */
const css = `
  .fmg-lead { margin: 0 0 12px; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-dark); }
  .fmg-card { border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 10px 12px; background: var(--ew-n-50); margin: 0 0 14px; }
  .fmg-meta { display: flex; flex-wrap: wrap; gap: 4px 10px; margin: 0 0 6px; font: var(--ew-fw-medium) 11.5px/1.3 var(--ew-font); color: var(--ew-n-500); }
  .fmg-msg { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-dark); }
  .fmg-muted { margin: 6px 0 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .fmg-set { border: 0; margin: 0; padding: 0; min-width: 0; }
  .fmg-legend { padding: 0; margin: 0 0 6px; font: var(--ew-fw-semibold) 13px/1.3 var(--ew-font); color: var(--ew-dark); }
  .fmg-list { display: flex; flex-direction: column; gap: 6px; margin: 0 0 12px; }
  .fmg-opt { display: flex; gap: 10px; align-items: flex-start; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 8px 10px; background: #fff; cursor: pointer; }
  .fmg-opt.on { border-color: var(--ew-green); background: var(--ew-n-50); }
  .fmg-opt input { margin-top: 3px; }
  .fmg-opt-body { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .fmg-opt-meta { font: var(--ew-fw-medium) 11px/1.3 var(--ew-font); color: var(--ew-n-500); }
  .fmg-opt-text { overflow-wrap: anywhere; font: var(--ew-fw-regular) 13px/1.4 var(--ew-font); color: var(--ew-dark); }
  .fmg-search { width: 100%; box-sizing: border-box; padding: 7px 10px; margin: 0 0 8px; border: 1px solid var(--ew-n-300, #C9CDD1); border-radius: var(--ew-r-md); font: var(--ew-fw-regular) 13px/1.3 var(--ew-font); }
  .fmg-preview { border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 10px 12px; margin: 4px 0 0; }
  .fmg-preview h3 { margin: 0 0 6px; font: var(--ew-fw-semibold) 13px/1.3 var(--ew-font); color: var(--ew-dark); }
  .fmg-lines { margin: 8px 0 0; padding-left: 18px; font: var(--ew-fw-regular) 12.5px/1.5 var(--ew-font); color: var(--ew-dark); }
  .fmg-lines li.warn { color: var(--ew-danger); font-weight: var(--ew-fw-semibold); }
  .fmg-cant { margin: 10px 0 0; font: var(--ew-fw-regular) 12.5px/1.45 var(--ew-font); color: var(--ew-danger); }
  .fmg-result { margin: 0 0 8px; padding: 10px 12px; border-radius: var(--ew-r-md); border: 1px solid var(--ew-n-200); font: var(--ew-fw-regular) 13px/1.5 var(--ew-font); color: var(--ew-dark); }
  .fmg-result.warn { border-color: var(--ew-warning); }
`;

/** After a request that failed: it may have been applied before it did, so the list (refreshed now) is where to see. */
const FAILED_SO_LOOK = 'If any of it was applied before this failed, the list now shows it.';

/**
 * A failed request, in the API's own words. ⚠️ THE HEDGE ONLY WHERE IT IS TRUE, as for the board dialogs: a refusal the API made before it wrote anything
 * (a 400, 403, 404, 409, 424: every merge refusal is one of those) is certain, and "if any of it was applied" would contradict it. A dropped connection or a
 * 5xx says nothing about whether the merge happened.
 */
function FailureNote({ error }: { error: unknown }) {
  return <p className="fmg-cant" role="alert">{(error as Error).message}{mayHaveBeenApplied(error) ? ` ${FAILED_SO_LOOK}` : ''}</p>;
}

function TargetOption({ b, chosen, disabled, onChoose }: { b: BoardItem; chosen: boolean; disabled: boolean; onChoose: (id: number) => void }) {
  const wants = wantsLabel(b.followerCount);
  return (
    <label className={`fmg-opt${chosen ? ' on' : ''}`}>
      <input type="radio" name="merge-target" checked={chosen} disabled={disabled} onChange={() => onChoose(b.id)} />
      <span className="fmg-opt-body">
        <span className="fmg-opt-meta">
          {CATEGORY_LABEL[b.category] ?? b.category}{wants ? ` · ${wants}` : ''}{b.replies.length > 0 ? ` · ${plural(b.replies.length, 'reply', 'replies')}` : ''} · {fmtWhen(b.createdAt)}
        </span>
        <span className="fmg-opt-text">{snippet(b.message, 220)}</span>
      </span>
    </label>
  );
}

/**
 * Merge an item into another request an admin found it duplicates. Choose the other request (suggested from the board by the same matcher the members' "Similar
 * requests" use, with looser limits, or found by searching it), read what will happen, and confirm. Nothing is changed until the admin confirms, and
 * ⚠️ IT CANNOT BE UNDONE FROM HERE: the preview says so.
 *
 * Only a request on the board that is still open is on offer (`mergeCandidates`): the API refuses any other, because everyone merged into an item reads
 * its text, and a closed item would never tell them anything. The API still decides, and what it refuses with is shown as it is.
 */
export function MergeDialog({ item, items, onClose }: { item: FeedbackItem; items: readonly FeedbackItem[]; onClose: () => void }) {
  const qc = useQueryClient();
  // ⚠️ ALWAYS LOOKED AT AGAIN ON OPEN. The board is cached for two minutes and shared with the members' Everyone's feedback, so what is chosen here would otherwise
  // come from a list that may have a request already closed or hidden, and the admin would find out from a refusal after pressing Merge.
  const board = useQuery({ queryKey: BOARD_KEY, queryFn: feedbackApi.board, refetchOnMount: 'always' });
  // ⚠️ AND WHAT IT HOLDS UNTIL THEN IS NOT OFFERED. The cached list is still "data" while that look-again is on its way, so `isLoading` is false and a request
  // that has closed or been hidden since is selectable for the length of one round trip. Until the first fetch since the dialog opened has answered, it is loading.
  // (A failure counts as an answer: `isFetchedAfterMount` is true after an error too, and the error is what is shown.)
  const loadingBoard = board.isLoading || !board.isFetchedAfterMount;
  const [chosenId, setChosenId] = useState<number | null>(null);
  const [query, setQuery] = useState('');

  const candidates = useMemo(() => mergeCandidates(board.data ?? [], item.id), [board.data, item.id]);
  const index = useMemo(() => indexCandidates(candidates), [candidates]);
  const suggestions = useMemo(() => suggestTargets(index, item.message), [index, item.message]);
  const found = useMemo(() => searchTargets(candidates, query), [candidates, query]);
  // A request that matches the search AND was suggested is listed once, in the suggestions: the same request twice would be two radios in one group, both checked.
  const suggestedIds = useMemo(() => new Set(suggestions.map((s) => s.item.id)), [suggestions]);
  const extra = found.shown.filter((b) => !suggestedIds.has(b.id));
  const chosen = candidates.find((c) => c.id === chosenId);
  // The other request's issue, when the admin's list has it: the board does not carry it, so the preview says "the other request" without it.
  const targetIssue = chosenId != null ? items.find((o) => o.id === chosenId)?.githubIssueNumber ?? null : null;
  const hadIssue = item.githubIssueNumber != null;
  // The admin's list is read again after a merge, and after a request that may have been applied (a dropped connection): if THIS item shows as merged there
  // now, whether by this admin's attempt or another admin's, there is nothing more to do, and pressing Merge again would only be refused.
  const mergedMeanwhile = item.duplicateOf == null && items.find((o) => o.id === item.id)?.duplicateOf != null;

  const run = useMutation({
    mutationFn: (intoId: number) => feedbackApi.merge(item.id, intoId),
    // ⚠️ SETTLED, not only success: a request that failed part-way (a dropped connection) may have merged, and a list that was not refreshed would still offer to.
    // The board changes too (the other request has more followers; nothing here is on it any more), and so does everybody's My feedback.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      void qc.invalidateQueries({ queryKey: MINE_KEY });
      void qc.invalidateQueries({ queryKey: BOARD_KEY });
    },
  });
  const result = run.data ?? null;
  const notes = result ? describeMerge(result, hadIssue) : [];

  return (
    <Modal
      isOpen
      onClose={run.isPending ? () => undefined : onClose}
      title={result ? mergeHeading(result, hadIssue) : 'Merge into another request?'}
      maxWidth={820}
      closeOnBackdropClick={false}
      footer={result ? (
        <button className="btn btn-primary" onClick={onClose}>Close</button>
      ) : (
        <>
          <button className="btn btn-secondary" disabled={run.isPending} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!chosen || run.isPending || mergedMeanwhile} onClick={() => chosen && run.mutate(chosen.id)}>
            {run.isPending ? 'Merging…' : 'Merge'}
          </button>
        </>
      )}
    >
      <style>{css}</style>
      {result ? (
        <div role="status">
          {notes.map((n, i) => <p key={i} className={`fmg-result${n.tone === 'warn' ? ' warn' : ''}`}>{n.text}</p>)}
        </div>
      ) : (
        <>
          <p className="fmg-lead">
            Use this when a request asks for the same thing as another one that is already on the board. This one is closed as a duplicate and leaves the board, and the
            people who followed it follow the other one instead, so they hear when it is answered.
          </p>

          <div className="fmg-card" role="group" aria-label="The request being merged">
            <p className="fmg-meta">
              <span>{CATEGORY_LABEL[item.category] ?? item.category}</span>
              <span>{statusLabel(item.status, item.closedReason)}</span>
              {item.submitterName && <span>Sent by {item.submitterName}</span>}
              <span>{fmtWhen(item.createdAt)}</span>
            </p>
            <p className="fmg-msg">{item.message}</p>
          </div>

          {mergedMeanwhile && (
            <p className="fmg-cant" role="alert">
              This request has been merged into another one in the meantime. Look at the list: there is nothing more to do here.
            </p>
          )}

          {loadingBoard ? (
            <p className="fmg-muted" role="status">Loading the board…</p>
          ) : board.isError ? (
            <p className="fmg-cant" role="alert">
              The board could not be loaded, so there is nothing to choose from.{' '}
              <button type="button" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => void board.refetch()}>Try again</button>
            </p>
          ) : candidates.length === 0 ? (
            <p className="fmg-muted">
              No open request is on the board, so there is nothing to merge this into. A request can only be merged into one that is on the board and still open: show it on
              the board first.
            </p>
          ) : (
            <fieldset className="fmg-set">
              <legend className="fmg-legend">Merge it into</legend>
              {suggestions.length > 0 ? (
                <>
                  <p className="fmg-muted" style={{ margin: '0 0 6px' }}>Open requests on the board that read like this one:</p>
                  <div className="fmg-list">
                    {suggestions.map((s) => <TargetOption key={s.item.id} b={s.item} chosen={s.item.id === chosenId} disabled={run.isPending} onChoose={setChosenId} />)}
                  </div>
                </>
              ) : (
                <p className="fmg-muted" style={{ margin: '0 0 10px' }}>No open request on the board reads much like this one. Search the board for a word or two.</p>
              )}
              <input
                type="search"
                className="fmg-search"
                aria-label="Search the open requests on the board"
                placeholder="Search the open requests on the board"
                value={query}
                disabled={run.isPending}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query.trim() !== '' && (
                found.total === 0 ? (
                  <p className="fmg-muted" role="status">No open request on the board has all of those words.</p>
                ) : extra.length === 0 ? (
                  <p className="fmg-muted" role="status">Everything that matches is already listed above.</p>
                ) : (
                  <>
                    <div className="fmg-list">
                      {extra.map((b) => <TargetOption key={b.id} b={b} chosen={b.id === chosenId} disabled={run.isPending} onChoose={setChosenId} />)}
                    </div>
                    {found.total > found.shown.length && (
                      <p className="fmg-muted" role="status">Showing the first {SEARCH_SHOWN} of {found.total}. Type more words to narrow it.</p>
                    )}
                  </>
                )
              )}
            </fieldset>
          )}

          {chosenId != null && !chosen && !loadingBoard && (
            <p className="fmg-cant" role="alert">That request is no longer open on the board. Choose another.</p>
          )}

          {chosen && (
            <section className="fmg-preview" aria-label="What will happen">
              <h3>Merge it into</h3>
              <p className="fmg-msg">{chosen.message}</p>
              <p className="fmg-muted">
                {[wantsLabel(chosen.followerCount), chosen.replies.length > 0 ? plural(chosen.replies.length, 'reply', 'replies') : ''].filter(Boolean).join(' · ') || 'Nobody follows it yet'}
              </p>
              <h3 style={{ marginTop: 12 }}>What will happen</h3>
              <ul className="fmg-lines">
                {previewLines(item, targetIssue).map((l, i) => <li key={i} className={l.tone === 'warn' ? 'warn' : undefined}>{l.text}</li>)}
              </ul>
            </section>
          )}

          {run.isError && <FailureNote error={run.error} />}
        </>
      )}
    </Modal>
  );
}
